import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import Select from "react-select"
import { useSelector } from "react-redux"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import PasswordRequirementsHint from "../../../components/OnboardingComponents/PasswordRequirementsHint"
import {
  ChevronDropdown,
  customStyles,
} from "../../../components/OnboardingComponents/RoleSelect"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { reportFormApiError } from "../../../helpers/formFieldErrors"
import {
  getCityAdminCitySelectOptions,
  isCityAdminUser,
} from "../../../helpers/cityPortalRoles"
import {
  PASSWORD_COMPLEXITY,
  PASSWORD_REQUIREMENTS,
  getPasswordFieldError,
  getPasswordHelperTone,
} from "../../../helpers/passwordRules"
import {
  buildStaffFormData,
  mapCitySelectOptions,
  mapRoleSelectOptions,
} from "./adminStaffData"
import StaffCityMultiSelect from "./StaffCityMultiSelect"
import defaultAvatar from "../../../assets/images/admin/avatar.png"
import ProfilePhoto from "../../Profile/ProfilePhoto"
import { getUserFullName } from "../../../helpers/userDisplay"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const MAX_PHOTO_BYTES = 20 * 1024 * 1024

const INITIAL_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  passwordConfirmation: "",
  roleId: "",
  cityIds: [],
}

const validationSchema = yup.object().shape({
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  email: yup
    .string()
    .trim()
    .email("Enter a valid email address")
    .required("Email address is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(PASSWORD_COMPLEXITY, PASSWORD_REQUIREMENTS),
  passwordConfirmation: yup
    .string()
    .required("Please confirm the password")
    .oneOf([yup.ref("password")], "Passwords must match"),
  roleId: yup.string().required("Role is required"),
  cityIds: yup
    .array()
    .of(yup.string())
    .min(1, "Select at least one city")
    .required("City is required"),
})

const findOption = (options, value) =>
  options.find(option => option.value === value) || null

const StaffFormSelect = ({
  id,
  label,
  options,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  isLoading = false,
  isSearchable = true,
}) => {
  const hasError = Boolean(error)
  const selectStyles = customStyles(hasError)

  return (
    <div className={`amok-field ${hasError ? "amok-field--error" : ""}`.trim()}>
      <label className="amok-field__label" htmlFor={id}>
        {label}
      </label>
      <Select
        inputId={id}
        className={`amok-role-select ${hasError ? "amok-role-select--error" : ""}`}
        classNamePrefix="amok-role-select"
        options={options}
        value={findOption(options, value)}
        onChange={option => onChange(option?.value || "")}
        onBlur={onBlur}
        placeholder={placeholder}
        isLoading={isLoading}
        isSearchable={isSearchable}
        isClearable={false}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        components={{
          DropdownIndicator: ChevronDropdown,
          IndicatorSeparator: null,
        }}
        styles={{
          ...selectStyles,
          menuPortal: base => ({ ...base, zIndex: 10060 }),
        }}
      />
      {hasError ? (
        <p className="amok-field__message amok-field__message--error">{error}</p>
      ) : null}
    </div>
  )
}

StaffFormSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  placeholder: PropTypes.string,
  isLoading: PropTypes.bool,
  isSearchable: PropTypes.bool,
}

StaffFormSelect.defaultProps = {
  onBlur: undefined,
  error: "",
  placeholder: "Select…",
  isLoading: false,
  isSearchable: true,
}

/**
 * Create Staff Member modal — POST /api/v1/admin/staff/create
 */
const CreateStaffModal = ({ isOpen, onClose, onSuccess }) => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const isCityAdmin = isCityAdminUser(currentUser)
  const cityAdminOptions = useMemo(
    () => getCityAdminCitySelectOptions(currentUser),
    [currentUser],
  )
  const [roleOptions, setRoleOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [profileImage, setProfileImage] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      setProfileImage(null)
      setPhotoPreview(prev => {
        if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev)
        return null
      })
      return undefined
    }

    let cancelled = false
    const loadOptions = async () => {
      setOptionsLoading(true)
      try {
        const rolesPromise = get(endpoints.getAdminRoles, {
          params: { page: 1, page_size: 100 },
        })
        const citiesPromise = isCityAdmin
          ? Promise.resolve(null)
          : get(endpoints.getAdminCities, {
              params: { page: 1, page_size: 100 },
            })

        const [rolesResponse, citiesResponse] = await Promise.all([
          rolesPromise,
          citiesPromise,
        ])

        if (cancelled) return

        if (rolesResponse?.data?.success) {
          setRoleOptions(
            mapRoleSelectOptions(rolesResponse.data.result?.data || []),
          )
        } else {
          toastApiError(rolesResponse, "Failed to load roles")
        }

        if (isCityAdmin) {
          setCityOptions(cityAdminOptions)
        } else if (citiesResponse?.data?.success) {
          setCityOptions(
            mapCitySelectOptions(citiesResponse.data.result?.data || []),
          )
        } else {
          toastApiError(citiesResponse, "Failed to load cities")
        }
      } catch (err) {
        if (!cancelled) toastApiError(err, "Failed to load form options")
      } finally {
        if (!cancelled) setOptionsLoading(false)
      }
    }

    loadOptions()
    return () => {
      cancelled = true
    }
  }, [cityAdminOptions, isCityAdmin, isOpen])

  useEffect(
    () => () => {
      if (photoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview)
      }
    },
    [photoPreview],
  )

  const handlePhotoChange = event => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo must be 20MB or smaller.", TOAST_OPTIONS)
      event.target.value = ""
      return
    }

    if (photoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(photoPreview)
    }

    setProfileImage(file)
    setPhotoPreview(URL.createObjectURL(file))
    event.target.value = ""
  }

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setFieldError, setFieldTouched },
  ) => {
    try {
      const formData = buildStaffFormData({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
        roleId: values.roleId,
        cityIds: values.cityIds,
        profileImage,
        includeEmail: true,
        includePassword: true,
        includeNotes: false,
      })

      const response = await post(endpoints.createAdminStaff, formData, {
        skipErrorToast: true,
      })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        reportFormApiError(
          response,
          "Failed to create staff member",
          { setFieldError, setFieldTouched },
          {
            first_name: "firstName",
            last_name: "lastName",
            password_confirmation: "passwordConfirmation",
            role_id: "roleId",
            city_ids: "cityIds",
          },
        )
        return
      }

      toast.success(
        response?.data?.message || "Staff member created successfully.",
        TOAST_OPTIONS,
      )
      resetForm()
      setProfileImage(null)
      if (photoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview)
      }
      setPhotoPreview(null)
      onSuccess?.()
      onClose()
    } catch (err) {
      reportFormApiError(
        err,
        "Failed to create staff member",
        { setFieldError, setFieldTouched },
        {
          first_name: "firstName",
          last_name: "lastName",
          password_confirmation: "passwordConfirmation",
          role_id: "roleId",
          city_ids: "cityIds",
        },
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--password admin-staff-modal"
      contentClassName="amok-modal__content admin-staff-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-staff-modal__body">
        <Formik
          key={isOpen ? "create-staff-open" : "create-staff-closed"}
          initialValues={INITIAL_VALUES}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="admin-staff-modal__form">
              <div className="admin-staff-modal__intro">
                <h3 className="admin-staff-modal__title">Create Staff Member</h3>
                <p className="admin-staff-modal__subtitle">
                  Add a new team member and set their role.
                </p>
              </div>

              <div className="admin-staff-modal__fields">
                <div className="admin-staff-modal__photo-row">
                  <ProfilePhoto
                    src={photoPreview || defaultAvatar}
                    name={
                      getUserFullName(
                        {
                          first_name: values.firstName,
                          last_name: values.lastName,
                        },
                        { fallback: "Staff" },
                      )
                    }
                    className="admin-staff-modal__photo"
                  />
                  <div className="admin-staff-modal__photo-actions">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      className="admin-staff-modal__file-input"
                      onChange={handlePhotoChange}
                    />
                    <CustomButton
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting}
                    >
                      Upload Photo
                    </CustomButton>
                    <p className="admin-staff-modal__photo-hint">
                      JPG or PNG. Max size of 20MB
                    </p>
                  </div>
                </div>

                <div className="admin-staff-modal__row">
                  <CustomInput
                    label="First Name"
                    name="firstName"
                    placeholder="Enter first name"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("firstName", true)}
                    error={touched.firstName && errors.firstName}
                  />
                  <CustomInput
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter last name"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("lastName", true)}
                    error={touched.lastName && errors.lastName}
                  />
                </div>
                <CustomInput
                  label="Email Address"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched("email", true)}
                  error={touched.email && errors.email}
                />
                <div className="admin-staff-modal__row">
                  <div className="edit-profile__password-field">
                    <CustomInput
                      label="Password"
                      name="password"
                      type="password"
                      placeholder="Enter password"
                      value={values.password}
                      onChange={event => {
                        handleChange(event)
                        setFieldTouched("password", true, false)
                      }}
                      onBlur={() => setFieldTouched("password", true)}
                      error={getPasswordFieldError(
                        touched.password,
                        errors.password,
                      )}
                      helperTone={getPasswordHelperTone(values.password)}
                    />
                    <PasswordRequirementsHint value={values.password} />
                  </div>
                  <CustomInput
                    label="Confirm Password"
                    name="passwordConfirmation"
                    type="password"
                    placeholder="Confirm password"
                    value={values.passwordConfirmation}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("passwordConfirmation", true)}
                    error={
                      touched.passwordConfirmation && errors.passwordConfirmation
                    }
                  />
                </div>
                <StaffFormSelect
                  id="create-staff-role"
                  label="Select Role"
                  options={roleOptions}
                  value={values.roleId}
                  onChange={next => setFieldValue("roleId", next)}
                  onBlur={() => setFieldTouched("roleId", true)}
                  error={touched.roleId && errors.roleId}
                  placeholder="Select role"
                  isLoading={optionsLoading}
                  isSearchable={false}
                />
                <StaffCityMultiSelect
                  id="create-staff-cities"
                  label="Select City"
                  options={cityOptions}
                  value={values.cityIds}
                  onChange={next => {
                    setFieldValue("cityIds", next)
                    setFieldTouched("cityIds", true, false)
                  }}
                  onBlur={() => setFieldTouched("cityIds", true)}
                  error={touched.cityIds && errors.cityIds}
                  isLoading={optionsLoading}
                />
              </div>

              <div className="admin-staff-modal__actions">
                <CustomButton
                  variant="danger"
                  size="md"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting || optionsLoading}
                >
                  Create Staff
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

CreateStaffModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}

CreateStaffModal.defaultProps = {
  onSuccess: undefined,
}

export default CreateStaffModal
